import "overlayscrollbars/overlayscrollbars.css";
import { createApp, watchEffect } from "vue";
import { createPinia } from "pinia";
import Popper from "vue3-popper";
import App from "./App.vue";
import { Router } from "vue-router";
import "@/assets/main.css";
import i18n from "@/modules/i18n";
import { useStoreMarketplace } from "./stores/storeMarketplace";
import { createHead } from "@vueuse/head";
import * as Sentry from "@sentry/vue";
import "unfonts.css";
import { useStoreApp } from "@/stores/storeApp";
import { useStoreUserV2 } from "./stores/storeUserV2";
import usePOLPrice from "./stores/POLprice";
import useUsdEuroRate from "./stores/storeUsdEuroRate";
import router from "./router";
import { VueQueryPlugin } from "@tanstack/vue-query";

const app = createApp(App);
export const pinia = createPinia();
const head = createHead();

import socketService from "./services/socketService";
import { useWeb3AuthStore } from "./stores/storeWeb3auth";
import { ADAPTER_EVENTS } from "@web3auth/base";

// Initialize the global socket connection
socketService.initialize();

Sentry.init({
  app,
  dsn: "https://a53dec8b3546e57defd94ff61dc66e5a@o4507073322156032.ingest.de.sentry.io/4507073323401296",
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/marketplace.fungiball\.io/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  denyUrls: ["http://localhost:5173"],
});

declare module "pinia" {
  export interface PiniaCustomProperties {
    router: Router;
  }
}

pinia.use(({ store }) => {
  store.router = router;
});

app.use(VueQueryPlugin);

app.use(i18n);
app.use(pinia);
app.use(router);
app.use(head);
app.component("popper", Popper);
app.mount("#app");

const web3AuthStore = useWeb3AuthStore();

const waitForWeb3Auth = () => {
  return new Promise<boolean>((resolve) => {
    const web3AuthStore = useWeb3AuthStore();
    web3AuthStore.initialize();

    // If already connected, resolve immediately
    if (web3AuthStore.isConnected) {
      resolve(true);
      return;
    }

    let stop: (() => void) | undefined;

    const timeout = setTimeout(() => {
      stop?.();
      console.log("Web3Auth timeout");
      resolve(false);
    }, 30000);

    stop = watchEffect(() => {
      if (web3AuthStore.isInitialized) {
        console.log("Web3Auth initialized");
        clearTimeout(timeout);
        stop?.();
        resolve(true);
      } else if (web3AuthStore.status === ADAPTER_EVENTS.READY) {
        console.log("Web3Auth ready");
        clearTimeout(timeout);
        stop?.();
        resolve(false);
      }
    });
  });
};

let initialized = false;

router.beforeEach(async (to) => {
  const storeApp = useStoreApp();
  const storeMarketplace = useStoreMarketplace();
  const userStore = useStoreUserV2();

  if (to.query.ref) {
    console.log("Referral code found in URL:", to.query.ref);
    const refCode = to.query.ref as string;
    userStore.setRefCode(refCode);
  }

  if (!initialized) {
    initialized = true;

    await storeMarketplace.init();
    await usePOLPrice().initialize();
    await useUsdEuroRate().initialize();

    // Wait for Web3Auth to be ready
    await waitForWeb3Auth();

    console.log("🔄 init user before routing...");

    await storeApp.init();
  }

  if (to.meta.isAuth && !userStore.isConnected) {
    userStore.logout();
    storeApp.showAuthModal = true;
    return { path: "" };
  }
});
